"use client";

import { Button } from "@/components/ui/button";
import {
  ClockFilled,
  ArrowRight,
  VerifiedFilled,
  Target,
} from "@/components/ui/icons";
import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { CardContent, CardFooter, CardHeader } from "@/app/(v1)/ui/card";
import { InterviewParams } from "../../utils";
import { variants, layoutIds } from "../../animations/card-animations";
import { useInterviewStoreContext } from "../../hooks/use-interview-store";
import { useI18n } from "../../../../locales/client";
import { createNeedleRenderer } from "../../../../locales/needle-renderer";
import { useCustomAnalytics } from "@/app/api/analytics/client";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  className = "",
}) => {
  return (
    <div
      className={cn(
        `group h-full relative bg-white rounded-sm p-4 sm:p-5 border border-gray-200/50 hover:border-gray-200 hover:transition-none transition-all duration-200`,
        className,
      )}
    >
      <div className="relative z-10">
        {icon}
        <h3 className="font-semibold text-gray-900 text-md mb-1">{title}</h3>
        <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

// Separate client component to handle search params
export function WelcomeScreen(props: {
  data: InterviewParams;
  onNext: () => void;
}) {
  const context = useInterviewStoreContext();
  const t = useI18n();
  const renderNeedles = useMemo(
    () =>
      createNeedleRenderer({
        highlight: (value) =>
          `<span class="text-[hsl(var(--brand-bg-color))] font-semibold bg-[hsl(var(--brand-bg-color))]/5 px-1.5 py-0.5 rounded-sm">${value}</span>`,
      }),
    [],
  );
  const brandAssets = context?.brandedAssets ?? {
    logoUrl: null,
    colors: {
      background: "hsl(0, 0%, 0%)",
      foreground: "hsl(0, 0%, 100%)",
    },
  };

  const companyName = props.data.company.name;

  const analytics = useCustomAnalytics({
    companyId: props.data.company.id,
    userId: props.data.user.id,
  });

  // Initialize interview store and identify user with PostHog
  useEffect(() => {
    analytics.identifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    analytics.trackPageView("welcome_screen");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = () => {
    analytics.candidateIntentionGetStarted();

    props.onNext();
  };

  return (
    <>
      <motion.div
        layoutId={layoutIds.card.header}
        variants={variants.card.header}
        initial="initial"
        animate="animate"
      >
        <CardHeader className="mx-auto relative z-10">
          {brandAssets.logoUrl && (
            <Image
              alt={`${companyName} logo`}
              src={brandAssets.logoUrl}
              width={120}
              height={120}
              className="mb-10 mt-3"
            />
          )}
        </CardHeader>
      </motion.div>

      <motion.div
        layoutId={layoutIds.card.content}
        variants={variants.card.content}
        initial="initial"
        animate="animate"
      >
        <CardContent>
          {/* Header with icon */}
          <div className="text-left ~mb-8/14">
            <h1 className="text-3xl font-semibold mb-2 sm:mb-3 leading-tight">
              {t("home.welcomeText", { companyName })}
            </h1>

            <p
              className="text-xl text-gray-700 mb-1 sm:mb-2 max-w-[65ch] leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: renderNeedles(t("home.welcomeDescription")),
              }}
            />
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-4 sm:mb-6"
            variants={variants.list.container}
            initial="initial"
            animate="animate"
          >
            <motion.div variants={variants.list.item}>
              <FeatureCard
                icon={
                  <div className="w-8 h-8 bg-emerald-50/60 rounded-sm flex items-center justify-center mb-3">
                    <VerifiedFilled className="size-5 text-emerald-600" />
                  </div>
                }
                title={t("home.cards.fairAssesment.title")}
                description={t("home.cards.fairAssesment.description")}
              />
            </motion.div>

            <motion.div variants={variants.list.item}>
              <FeatureCard
                icon={
                  <div className="w-8 h-8 bg-red-50/60 rounded-sm flex items-center justify-center mb-3">
                    <Target className="size-5 text-red-600" />
                  </div>
                }
                title={t("home.cards.detailedFeedback.title")}
                description={t("home.cards.detailedFeedback.description")}
              />
            </motion.div>

            <motion.div variants={variants.list.item}>
              <FeatureCard
                icon={
                  <div className="w-8 h-8 bg-gray-50/80 rounded-sm flex items-center justify-center mb-3">
                    <ClockFilled className="size-5 text-gray-700" />
                  </div>
                }
                title={t("home.cards.duration.title")}
                description={t("home.cards.duration.description")}
              />
            </motion.div>
          </motion.div>
        </CardContent>
      </motion.div>

      <motion.div
        layoutId={layoutIds.card.footer}
        variants={variants.card.footer}
        initial="initial"
        animate="animate"
      >
        <CardFooter className="flex flex-col-reverse sm:flex-row items-center justify-end w-full">
          <motion.div
            layoutId={layoutIds.cta}
            className="order-1 sm:order-2 w-full sm:w-auto"
            variants={variants.cta}
            initial="initial"
            animate="animate"
          >
            <Button onClick={handleNext} size={"xl"} variant={"branded"}>
              <span className="relative z-10 flex items-center justify-center gap-4">
                {t("home.cards.cta")}
                <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Button>
          </motion.div>
        </CardFooter>
      </motion.div>
    </>
  );
}
